import { useEffect, useState } from "react";
import type { CheckoutCart } from "./checkout";

/** Query flag for a local UI-only setup walkthrough (never on prod/public hosts). */
export const MOCK_PAID_QUERY = "mock";

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

export type StripePublishableMode = "test" | "live" | "unset" | "invalid";

export function stripePublishableKey(): string {
  return (process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "").trim();
}

export function stripePublishableMode(
  key = stripePublishableKey(),
): StripePublishableMode {
  if (!key) return "unset";
  if (key.startsWith("pk_test_")) return "test";
  if (key.startsWith("pk_live_")) return "live";
  return "invalid";
}

export function isLoopbackHostname(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname.trim().toLowerCase());
}

export function isProductionRuntime(): boolean {
  return process.env.NODE_ENV === "production";
}

/**
 * Matches backend `allowDevMockCheckout` host + env gates.
 * Does not read `STRIPE_SECRET_KEY` (server-only). Mock-paid still requires
 * that secret to be blank — placeholders like `sk_test_REPLACE_ME` count as set.
 */
export function canUseDevMockCheckout(): boolean {
  if (isProductionRuntime()) return false;
  if (typeof window === "undefined") return false;
  return isLoopbackHostname(window.location.hostname);
}

export function useLocalCheckoutHost(): boolean {
  return useCheckoutHost().localDev;
}

export function useCheckoutHost(): { localDev: boolean; hostReady: boolean } {
  const [localDev, setLocalDev] = useState(false);
  const [hostReady, setHostReady] = useState(false);
  useEffect(() => {
    setLocalDev(canUseDevMockCheckout());
    setHostReady(true);
  }, []);
  return { localDev, hostReady };
}

export function mockPaidSetupHref(): string {
  return `/setup?${MOCK_PAID_QUERY}=1`;
}

export function isMockPaidSearchParam(value: string | null): boolean {
  return value === "1" || value === "true";
}

export type CheckoutSessionResult = {
  url?: string;
  sessionId?: string;
  mock?: boolean;
  error?: string;
  status: number;
};

/**
 * POST the cart shape already consumed by `/api/checkout/session`.
 * Frontend does not interpret Stripe secrets — only the returned Checkout URL.
 */
export async function requestCheckoutSession(
  cart: CheckoutCart,
): Promise<CheckoutSessionResult> {
  const res = await fetch("/api/checkout/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cart }),
  });
  let data: {
    url?: string;
    sessionId?: string;
    mock?: boolean;
    error?: string;
  } = {};
  try {
    data = (await res.json()) as typeof data;
  } catch {
    /* non-JSON body */
  }
  return { ...data, status: res.status };
}

export function isCheckoutFailClosed(result: CheckoutSessionResult): boolean {
  if (result.status === 503) return true;
  return Boolean(result.error && /not configured/i.test(result.error));
}

/** Surface 503 / fail-closed errors. Never invent a client mock-paid redirect. */
export function checkoutSessionErrorMessage(
  result: CheckoutSessionResult,
  localMockEligible: boolean,
): string {
  const base = result.error?.trim() || "Could not start checkout.";
  if (!localMockEligible) {
    return isCheckoutFailClosed(result)
      ? "Online payment isn’t available right now. Please contact us and we’ll complete your order."
      : base;
  }
  const status = result.status >= 400 ? `HTTP ${result.status}. ` : "";
  if (isCheckoutFailClosed(result)) {
    return `${status}${base} Local mock-paid requires a blank STRIPE_SECRET_KEY (empty). Placeholders like sk_test_REPLACE_ME count as set and skip mock. Production and public hosts fail-close — they never mock-pay.`;
  }
  return `${status}${base}`;
}
